import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Stack, Chip, IconButton, Tooltip, ToggleButton,
  ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  DeleteSweep as ClearIcon,
  PlayArrow as ResumeIcon,
  Pause as PauseIcon,
  Bolt as QuickIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { getSocket } from '../socket';
import { useDeviceSelection } from '../context/DeviceSelectionContext';

// ---------------------------------------------------------------------------
// Catalogs — the API surface this sandbox can exercise.
// ---------------------------------------------------------------------------

const TRANSPORTS = ['auto', 'wifi', '4g', 'uart', 'bluetooth'];

// Host -> device firmware command codes (sent as a raw packet via /device/debug).
const HOST_COMMANDS = [
  { code: 0x02, name: 'CMD_GET_DEVICE_READY', desc: 'Ask device to become ready (readiness probe)' },
  { code: 0x04, name: 'CMD_SEND_SW_PARAMETERS', desc: 'Send SW parameters (payload required)' },
  { code: 0x06, name: 'CMD_SET_DEVICE_SETTINGS', desc: 'Apply device settings (payload required)' },
  { code: 0x07, name: 'CMD_GET_DEVICE_SETTINGS', desc: 'Query device settings' },
  { code: 0x09, name: 'CMD_GET_FEEDBACK_INFO', desc: 'Request combined feedback info' },
  { code: 0x0f, name: 'CMD_ENTER_BOOTLOADER', desc: 'Enter bootloader (OTA) — use with care' },
  { code: 0x1d, name: 'CMD_EMERGENCY_STOP', desc: 'Emergency stop: halt cycle -> safe idle (mid-cycle safe)' },
  { code: 0x22, name: 'CMD_GET_ADC', desc: 'Request raw ADC data' },
  { code: 0x24, name: 'CMD_GET_STAT', desc: 'Request statistics' },
];

// Device -> host response codes, and the Socket.IO event each surfaces on.
const RESPONSE_CODES = [
  { code: 0x01, name: 'CMD_DEVICE_ONLINE', event: 'device-status / device-data' },
  { code: 0x03, name: 'CMD_DEVICE_IS_READY', event: 'device-ready' },
  { code: 0x05, name: 'CMD_DATA_ACK', event: 'device-ack' },
  { code: 0x08, name: 'CMD_DEVICE_SETTINGS', event: 'device-settings' },
  { code: 0x0a, name: 'CMD_FEEDBACK_INFO', event: 'feedback-info / device-feedback' },
  { code: 0x10, name: 'CMD_ELEMENT_PROGRESS', event: 'element-progress' },
  { code: 0x23, name: 'CMD_ADC_DATA', event: 'adc-data' },
  { code: 0x25, name: 'CMD_STAT_DATA', event: 'stat-data' },
];

// Every device response event we subscribe to for the live console.
const SOCKET_EVENTS = [
  'device-status', 'device-data', 'device-ready', 'device-ack', 'device-settings',
  'feedback-info', 'device-feedback', 'adc-data', 'stat-data', 'element-progress',
  'ota-status', 'device-error',
];

// REST endpoints reachable from the sandbox (relative to the /api baseURL).
const REST_APIS = [
  { m: 'POST', path: '/device/debug', desc: 'Send a raw packet (cmd + hex payload) to the device' },
  { m: 'POST', path: '/device/command', desc: 'High-level command (builds SW-params / settings payloads)' },
  { m: 'POST', path: '/device/ready', desc: 'Request device-ready (readiness probe)' },
  { m: 'GET', path: '/device/status', desc: 'Device status / ready' },
  { m: 'GET', path: '/device/settings', desc: 'Get device settings (awaits device-settings)' },
  { m: 'POST', path: '/device/settings', desc: 'Set device settings' },
  { m: 'POST', path: '/device/sw-parameters', desc: 'Send SWP element list' },
  { m: 'POST', path: '/device/vout-table', desc: 'Set Vout table for device' },
  { m: 'GET', path: '/device/feedback-info', desc: 'Request feedback info (awaits feedback-info)' },
  { m: 'GET', path: '/device/adc-data', desc: 'Request ADC data' },
  { m: 'GET', path: '/device/statistics', desc: 'Request statistics' },
  { m: 'GET', path: '/device/transport-mode', desc: 'Get transport mode' },
  { m: 'POST', path: '/device/transport-mode', desc: 'Set transport mode { mode }' },
  { m: 'GET', path: '/device/transport-status', desc: 'Transport connectivity (ws/serial/ble)' },
  { m: 'GET', path: '/device/devices/online', desc: 'List online device IDs' },
  { m: 'GET', path: '/ota/firmware-list', desc: 'List uploaded firmware (root, not /api)' },
  { m: 'GET', path: '/ota/precheck/:id', desc: 'OTA cycle-safety readiness check (root)' },
  { m: 'POST', path: '/ota/start', desc: 'Start OTA update (root)' },
];

// Quick actions: high-level REST calls scoped to the selected device + transport.
const QUICK_ACTIONS = [
  { label: 'Get Ready', method: 'post', url: '/device/ready', body: true },
  { label: 'Get Status', method: 'get', url: '/device/status' },
  { label: 'Get Settings', method: 'get', url: '/device/settings' },
  { label: 'Get Feedback', method: 'get', url: '/device/feedback-info' },
  { label: 'Get ADC', method: 'get', url: '/device/adc-data' },
  { label: 'Get Stats', method: 'get', url: '/device/statistics' },
  { label: 'Transport Mode', method: 'get', url: '/device/transport-mode', noDevice: true },
  { label: 'Transport Status', method: 'get', url: '/device/transport-status', noDevice: true },
];

const EVENT_COLORS = {
  'device-error': 'error',
  'device-ack': 'warning',
  'device-ready': 'success',
  'feedback-info': 'info',
  'device-feedback': 'info',
  'device-settings': 'secondary',
  'ota-status': 'primary',
};

const hex2 = (n) => '0x' + Number(n).toString(16).toUpperCase().padStart(2, '0');

function DeviceSandbox() {
  const { selectedDeviceId } = useDeviceSelection();
  const [onlineDevices, setOnlineDevices] = useState([]);
  const [targetDeviceId, setTargetDeviceId] = useState(selectedDeviceId || '');
  const [transport, setTransport] = useState('auto');
  const [connected, setConnected] = useState(false);

  // Raw packet builder
  const [rawCmd, setRawCmd] = useState('0x02');
  const [rawPayload, setRawPayload] = useState('');

  // Console
  const [log, setLog] = useState([]);
  const [paused, setPaused] = useState(false);
  const [onlyTarget, setOnlyTarget] = useState(false);
  const [showRef, setShowRef] = useState(false);

  const logEndRef = useRef(null);
  const idRef = useRef(0);
  // Refs so socket handlers read the latest values without re-subscribing.
  const pausedRef = useRef(paused);
  const onlyTargetRef = useRef(onlyTarget);
  const targetRef = useRef(targetDeviceId);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { onlyTargetRef.current = onlyTarget; }, [onlyTarget]);
  useEffect(() => { targetRef.current = targetDeviceId; }, [targetDeviceId]);

  const appendLog = useCallback((dir, label, data, event) => {
    idRef.current += 1;
    setLog(prev => {
      const next = [...prev, { id: idRef.current, ts: new Date(), dir, label, data, event }];
      return next.length > 500 ? next.slice(next.length - 500) : next;
    });
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const res = await api.get('/device/devices/online');
      const ids = Array.isArray(res.data) ? res.data : [];
      setOnlineDevices(ids);
      setTargetDeviceId(prev => prev || ids[0] || '');
    } catch (e) {
      appendLog('error', 'GET /device/devices/online failed', e.response?.data || e.message);
    }
  }, [appendLog]);

  useEffect(() => { refreshDevices(); }, [refreshDevices]);

  // Subscribe to all device response events for the live console.
  useEffect(() => {
    const socket = getSocket();
    setConnected(socket.connected);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    const handlers = SOCKET_EVENTS.map((evt) => {
      const h = (data) => {
        if (pausedRef.current) return;
        if (onlyTargetRef.current && targetRef.current) {
          const evDev = data && (data.deviceId || data.sourceDeviceId);
          // Keep events with no device id (they're broadcast/global), drop mismatches.
          if (evDev && String(evDev) !== String(targetRef.current)) return;
        }
        appendLog('in', evt, data, evt);
      };
      socket.on(evt, h);
      return [evt, h];
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      handlers.forEach(([evt, h]) => socket.off(evt, h));
    };
  }, [appendLog]);

  // Auto-scroll to newest entry unless paused.
  useEffect(() => {
    if (!paused) logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [log, paused]);

  const requireDevice = () => {
    if (!targetDeviceId) {
      appendLog('error', 'No target device selected', 'Pick a device (or connect one) first.');
      return false;
    }
    return true;
  };

  const sendRaw = async () => {
    if (!requireDevice()) return;
    const cmd = parseInt(rawCmd, rawCmd.trim().toLowerCase().startsWith('0x') ? 16 : 10);
    if (!cmd || Number.isNaN(cmd)) {
      appendLog('error', 'Invalid command', `"${rawCmd}" is not a valid cmd code.`);
      return;
    }
    const payload = rawPayload.replace(/\s+/g, '');
    const body = { cmd, payload, transport, deviceId: targetDeviceId };
    appendLog('out', `POST /device/debug ${hex2(cmd)}`, body);
    try {
      const res = await api.post('/device/debug', body);
      appendLog('info', `→ ${hex2(cmd)} sent`, res.data);
    } catch (e) {
      appendLog('error', `POST /device/debug failed`, e.response?.data || e.message);
    }
  };

  const runQuickAction = async (action) => {
    if (!action.noDevice && !requireDevice()) return;
    const params = action.noDevice ? {} : { deviceId: targetDeviceId, transport };
    appendLog('out', `${action.method.toUpperCase()} ${action.url}`, params);
    try {
      let res;
      if (action.method === 'get') {
        res = await api.get(action.url, { params });
      } else {
        res = await api.post(action.url, action.body ? { deviceId: targetDeviceId, transport } : {}, { params });
      }
      appendLog('info', `→ ${action.label} response`, res.data);
    } catch (e) {
      appendLog('error', `${action.label} failed`, e.response?.data || e.message);
    }
  };

  const dirColor = (entry) => {
    if (entry.dir === 'out') return 'primary.main';
    if (entry.dir === 'error') return 'error.main';
    if (entry.dir === 'info') return 'text.secondary';
    return 'text.primary';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Device Sandbox</Typography>
        <Chip size="small" color={connected ? 'success' : 'default'}
          label={connected ? 'socket connected' : 'socket offline'} />
        <Button size="small" variant="text" onClick={() => setShowRef(s => !s)}>
          {showRef ? 'Hide' : 'Show'} API reference
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Send commands to a live device and watch responses stream into the console. Raw packets
        go through <code>POST /device/debug</code>; quick actions call the high-level REST APIs.
        All are scoped to the selected device &amp; transport.
      </Typography>

      {showRef && <ApiReference />}

      {/* Target + transport */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Target Device</InputLabel>
            <Select label="Target Device" value={targetDeviceId}
              onChange={(e) => setTargetDeviceId(e.target.value)}>
              {onlineDevices.length === 0
                ? <MenuItem value="" disabled>No online devices</MenuItem>
                : onlineDevices.map(id => <MenuItem key={id} value={id}>{id}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Transport</InputLabel>
            <Select label="Transport" value={transport} onChange={(e) => setTransport(e.target.value)}>
              {TRANSPORTS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title="Refresh online devices">
            <IconButton onClick={refreshDevices}><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Quick actions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          <QuickIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Quick actions (high-level REST APIs)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {QUICK_ACTIONS.map(a => (
            <Button key={a.label} size="small" variant="outlined" onClick={() => runQuickAction(a)}>
              {a.label}
            </Button>
          ))}
        </Stack>
      </Paper>

      {/* Raw packet builder */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Raw packet builder → POST /device/debug</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Command</InputLabel>
            <Select label="Command" value={rawCmd} onChange={(e) => setRawCmd(e.target.value)}>
              {HOST_COMMANDS.map(c => (
                <MenuItem key={c.code} value={hex2(c.code)}>
                  {hex2(c.code)} — {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="cmd (hex/dec)" value={rawCmd} sx={{ width: 130 }}
            onChange={(e) => setRawCmd(e.target.value)} />
          <TextField size="small" label="payload (hex)" placeholder="e.g. AABBCC" fullWidth
            value={rawPayload} onChange={(e) => setRawPayload(e.target.value)} />
          <Button variant="contained" startIcon={<SendIcon />} onClick={sendRaw}
            disabled={!targetDeviceId}>Send</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {HOST_COMMANDS.find(c => hex2(c.code) === rawCmd)?.desc || 'Custom command code.'}
        </Typography>
      </Paper>

      {/* Live console */}
      <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: 1, borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Live event console ({log.length})</Typography>
          <ToggleButtonGroup size="small" exclusive value={onlyTarget}
            onChange={(_, v) => v !== null && setOnlyTarget(v)}>
            <ToggleButton value={false}>All devices</ToggleButton>
            <ToggleButton value={true}>Selected only</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={paused ? 'Resume' : 'Pause'}>
            <IconButton size="small" onClick={() => setPaused(p => !p)}>
              {paused ? <ResumeIcon /> : <PauseIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear console">
            <IconButton size="small" onClick={() => setLog([])}><ClearIcon /></IconButton>
          </Tooltip>
        </Box>
        <Box sx={{
          height: 360, overflowY: 'auto', p: 1.5, bgcolor: 'background.default',
          fontFamily: 'monospace', fontSize: '0.8rem',
        }}>
          {log.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
              No traffic yet. Send a command or wait for the device to emit.
            </Typography>
          )}
          {log.map(entry => (
            <Box key={entry.id} sx={{ mb: 1, pb: 1, borderBottom: '1px dashed', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                  {entry.ts.toLocaleTimeString()}
                </Typography>
                <Chip size="small" label={entry.dir === 'out' ? '▲ sent' : entry.dir === 'in' ? '▼ recv' : entry.dir}
                  color={entry.event ? (EVENT_COLORS[entry.event] || 'default') : (entry.dir === 'error' ? 'error' : 'default')}
                  variant={entry.dir === 'in' ? 'filled' : 'outlined'}
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                <Typography component="span" sx={{ fontFamily: 'inherit', color: dirColor(entry), fontWeight: 600 }}>
                  {entry.label}
                </Typography>
              </Box>
              {entry.data !== undefined && entry.data !== null && (
                <Box component="pre" sx={{
                  m: 0, mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  fontFamily: 'inherit', color: 'text.secondary',
                }}>
                  {typeof entry.data === 'string' ? entry.data : JSON.stringify(entry.data, null, 2)}
                </Box>
              )}
            </Box>
          ))}
          <div ref={logEndRef} />
        </Box>
      </Paper>
    </Box>
  );
}

// Collapsible reference of every API/command the sandbox touches.
function ApiReference() {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        This is the full device API surface. Device endpoints are under <code>/api/device</code>;
        OTA endpoints are at the server root (<code>/ota/*</code>).
      </Alert>
      <Box sx={{ overflowX: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom>REST endpoints</Typography>
        <Table size="small" sx={{ mb: 2, minWidth: 520 }}>
          <TableHead><TableRow>
            <TableCell>Method</TableCell><TableCell>Path</TableCell><TableCell>Description</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {REST_APIS.map(r => (
              <TableRow key={r.m + r.path}>
                <TableCell><Chip size="small" label={r.m} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.path}</TableCell>
                <TableCell>{r.desc}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" gutterBottom>Host → device command codes (raw sender)</Typography>
        <Table size="small" sx={{ mb: 2, minWidth: 520 }}>
          <TableHead><TableRow>
            <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Description</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {HOST_COMMANDS.map(c => (
              <TableRow key={c.code}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{hex2(c.code)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.name}</TableCell>
                <TableCell>{c.desc}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" gutterBottom>Device → host responses (observed in console)</Typography>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead><TableRow>
            <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Socket event</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {RESPONSE_CODES.map(c => (
              <TableRow key={c.code}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{hex2(c.code)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{c.event}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

export default DeviceSandbox;
